// commentThread — the PURE half of the shared CommentThread component
// (VaultOS v2 design §3.5).
//
// Everything here is a function of its arguments: no DOM, no SDK, no state.
// The component (components/CommentThread.js) owns the four
// `sdk.execute('comments', …)` calls and the rendering; this file owns the
// shape of a thread, so the ordering, the tombstone rule and the permission
// rule are unit-testable without a browser or a server.
//
// The row shape it produces:
//   { id, body, authorEmail, author, createdAt, editedAt, replyTo, deleted,
//     depth, ownedByMe, canRemove, replyCount }
//
// ── FA204 / frank trap ──────────────────────────────────────────────────────
// Every export is SELF-CONTAINED: it inlines its constants and never reads a
// module-scope const or calls a sibling export by its bare name. Same rule as
// dragRef.js / entityRef.js in this folder — see their headers for why.

// Normalize whatever the SDK handed back into a plain array of rows.
//
// `sdk.execute('comments', 'list', …)` resolves through BaseService._call,
// which already unwraps `{ success, data }` → `data`. But the dispatcher and
// the declarative fetch adapter both have shapes that wrap once more, and a
// caller that passes the raw response straight through is the common mistake —
// so accept the bare array, `{ data }`, `{ rows }` and `{ items }` and return
// [] for anything else. Never throws.
export const commentRowsFrom = (raw) => {
  if (Array.isArray(raw)) return raw
  if (!raw || typeof raw !== 'object') return []
  if (Array.isArray(raw.data)) return raw.data
  if (Array.isArray(raw.rows)) return raw.rows
  if (Array.isArray(raw.items)) return raw.items
  return []
}

// The display name for a comment's author.
//
// The server stores `authorEmail` and nothing else — there are no fabricated
// display names in this data model. The local part is the honest label; the
// caller renders its own polyglot fallback when this returns ''.
export const commentAuthorLabel = (comment) => {
  const email = comment && comment.authorEmail ? String(comment.authorEmail) : ''
  if (!email) return ''
  const at = email.indexOf('@')
  const local = at === -1 ? email : email.slice(0, at)
  if (!local) return email
  return local.charAt(0).toUpperCase() + local.slice(1)
}

// Absolute local date + time, the same convention ChatMessage's `Time` uses.
// Deliberately NOT a relative label ("5m ago"): a relative string needs a
// ticking re-render to stay true and a dozen plural-aware translation keys to
// be honest in any other language, and a comment thread is read, not watched.
// Returns '' for a missing/unparseable stamp so a row never prints 'Invalid
// Date'.
//
// `function`, not an arrow: `el.call('commentTimeLabel', …)` binds the element,
// and the month name has to read in the SESSION language — the shared
// `localizedDate` (Chrome has no Georgian names of its own). A caller with no
// element, or a registry without that function, keeps the browser-locale stamp.
export function commentTimeLabel(value) {
  if (value == null || value === '') return ''
  const d = new Date(value)
  const t = d.getTime()
  if (Number.isNaN(t)) return ''
  const opts = { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }
  const out = this && typeof this.call === 'function' ? this.call('localizedDate', d, opts) : ''
  return typeof out === 'string' && out ? out : d.toLocaleString([], opts)
}

// True when `viewer` may delete/edit `comment`.
//
// Mirrors the server's own row-level gate (CommentController._canManageOthers
// + the store): an author may always touch their OWN comment; only an editor
// or above may touch someone else's. The UI must not offer a button the
// server will refuse, and must not HIDE one it would allow.
//
// `viewer` = { email, canManageOthers }. A deleted row is never actionable —
// the tombstone is already the end state.
export const canManageComment = (comment, viewer) => {
  if (!comment || comment.deleted) return false
  const email = viewer && viewer.email ? String(viewer.email).toLowerCase() : ''
  const author = comment.authorEmail ? String(comment.authorEmail).toLowerCase() : ''
  if (email && author && email === author) return true
  return !!(viewer && viewer.canManageOthers)
}

// THE reducer: raw SDK response → the ordered, flat row list the thread
// renders.
//
// ORDERING is chronological with replies pulled up under their parent, and
// the nesting stops at ONE level. A comment thread on an entity is a
// conversation, not a tree; the second level of indentation buys nothing and
// costs the flat, borderless reading the design law asks for. A reply to a
// reply therefore renders at depth 1 beside its sibling, in time order, and
// still carries `replyTo` so nothing is lost.
//
// TOMBSTONES. The server soft-deletes so reply threads survive. A deleted
// comment that HAS replies keeps its slot (its children would otherwise
// dangle); a deleted comment with no replies is dropped entirely, because a
// permanent "deleted" placeholder is noise the reader cannot act on.
//
// `viewer` is threaded through so `ownedByMe` / `canRemove` are decided once,
// here, instead of in a render factory that would recompute them per paint.
export const commentThreadRows = (raw, viewer) => {
  // Self-contained unwrap (does NOT call the sibling commentRowsFrom — see the
  // FA204 note in the file header).
  let list = []
  if (Array.isArray(raw)) list = raw
  else if (raw && typeof raw === 'object') {
    if (Array.isArray(raw.data)) list = raw.data
    else if (Array.isArray(raw.rows)) list = raw.rows
    else if (Array.isArray(raw.items)) list = raw.items
  }

  const email = viewer && viewer.email ? String(viewer.email).toLowerCase() : ''
  const canManageOthers = !!(viewer && viewer.canManageOthers)

  const norm = []
  for (const c of list) {
    if (!c || typeof c !== 'object') continue
    const id = c._id != null ? String(c._id) : c.id != null ? String(c.id) : ''
    if (!id) continue
    const authorEmail = c.authorEmail ? String(c.authorEmail) : ''
    const author = (() => {
      if (!authorEmail) return ''
      const at = authorEmail.indexOf('@')
      const local = at === -1 ? authorEmail : authorEmail.slice(0, at)
      if (!local) return authorEmail
      return local.charAt(0).toUpperCase() + local.slice(1)
    })()
    const deleted = !!c.deleted
    const mine = !!(email && authorEmail && email === authorEmail.toLowerCase())
    norm.push({
      id,
      body: deleted ? '' : String(c.body == null ? '' : c.body),
      authorEmail,
      author,
      createdAt: c.createdAt || c.created_at || null,
      editedAt: c.editedAt || null,
      replyTo: c.replyTo != null ? String(c.replyTo) : null,
      deleted,
      depth: 0,
      ownedByMe: mine,
      canRemove: !deleted && (mine || canManageOthers),
      replyCount: 0
    })
  }

  // Chronological. A row with no stamp sorts last but keeps its arrival order
  // (a stable sort), so an optimistic row appended before the server answers
  // stays where the user saw it appear.
  const stamp = (r) => {
    const t = r.createdAt ? new Date(r.createdAt).getTime() : NaN
    return Number.isNaN(t) ? Infinity : t
  }
  norm.sort((a, b) => stamp(a) - stamp(b))

  const byId = new Map()
  for (const r of norm) byId.set(r.id, r)

  // Resolve each row to its ROOT ancestor and count replies on the way. A
  // reply whose parent is not in this thread (deleted hard, or paginated out)
  // is promoted to a root rather than dropped.
  const rootOf = new Map()
  for (const r of norm) {
    let cur = r
    let hops = 0
    while (cur.replyTo && byId.has(cur.replyTo) && hops < 64) {
      cur = byId.get(cur.replyTo)
      hops++
    }
    rootOf.set(r.id, cur.id)
    if (cur.id !== r.id) {
      r.depth = 1
      cur.replyCount++
    }
  }

  const roots = norm.filter((r) => rootOf.get(r.id) === r.id)
  const out = []
  for (const root of roots) {
    // A tombstone with no surviving replies is noise, not history.
    if (root.deleted && root.replyCount === 0) continue
    out.push(root)
    for (const r of norm) {
      if (r.id === root.id) continue
      if (rootOf.get(r.id) !== root.id) continue
      if (r.deleted) continue
      out.push(r)
    }
  }
  return out
}

// The POST body for `sdk.execute('comments', 'create', { payload })`.
//
// `entityRef` accepts the EntityRef object, the `ref:<type>:<id>` string form,
// or anything else parseEntityRef accepts — the parse is inlined here (FA204)
// so this file stays free of cross-file reads. Returns null when the ref or
// the body is missing, so a caller cannot post an unanchored comment.
export const commentCreatePayload = (entityRef, body, replyTo) => {
  const text = body == null ? '' : String(body).trim()
  if (!text) return null
  let type = ''
  let id = ''
  if (typeof entityRef === 'string') {
    const raw = entityRef.trim()
    if (raw.slice(0, 4) !== 'ref:') return null
    const rest = raw.slice(4)
    const cut = rest.indexOf(':')
    if (cut < 1) return null
    type = rest.slice(0, cut).trim()
    id = rest.slice(cut + 1).trim()
  } else if (entityRef && typeof entityRef === 'object') {
    if (!entityRef.type && typeof entityRef.ref === 'string') {
      const raw = entityRef.ref.trim()
      if (raw.slice(0, 4) !== 'ref:') return null
      const rest = raw.slice(4)
      const cut = rest.indexOf(':')
      if (cut < 1) return null
      type = rest.slice(0, cut).trim()
      id = rest.slice(cut + 1).trim()
    } else {
      type = entityRef.type == null ? '' : String(entityRef.type).trim()
      id = entityRef.id == null ? '' : String(entityRef.id).trim()
    }
  } else {
    return null
  }
  if (!type || !id) return null
  const parent = replyTo == null || replyTo === '' ? null : String(replyTo)
  return {
    entityRef: { type, id },
    body: text,
    ...(parent ? { replyTo: parent } : {})
  }
}

// The `{ entityType, entityId }` filter for
// `sdk.execute('comments', 'list', …)`. Null when the ref names nothing — the
// component then renders its empty state instead of firing an unscoped list
// the server would answer for the wrong entity.
export const commentListFilter = (entityRef) => {
  let type = ''
  let id = ''
  if (typeof entityRef === 'string') {
    const raw = entityRef.trim()
    if (raw.slice(0, 4) !== 'ref:') return null
    const rest = raw.slice(4)
    const cut = rest.indexOf(':')
    if (cut < 1) return null
    type = rest.slice(0, cut).trim()
    id = rest.slice(cut + 1).trim()
  } else if (entityRef && typeof entityRef === 'object') {
    if (!entityRef.type && typeof entityRef.ref === 'string') {
      const raw = entityRef.ref.trim()
      if (raw.slice(0, 4) !== 'ref:') return null
      const rest = raw.slice(4)
      const cut = rest.indexOf(':')
      if (cut < 1) return null
      type = rest.slice(0, cut).trim()
      id = rest.slice(cut + 1).trim()
    } else {
      type = entityRef.type == null ? '' : String(entityRef.type).trim()
      id = entityRef.id == null ? '' : String(entityRef.id).trim()
    }
  } else {
    return null
  }
  if (!type || !id) return null
  return { entityType: type, entityId: id }
}
